import React, { useEffect, useState, useRef } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import usePermissions from "../hook/userPermission";
import { getMyPermissions } from "../Apis/RoleAPIs";
import { getProfileCorrections } from "../Apis/ProfileCorrectionAPIs";

const MasterLayout = ({ children, disableAccessCheck = false }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const sidebarRef = useRef(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [pendingSupportCount, setPendingSupportCount] = useState(0);

  useEffect(() => {
    const updateCount = () => {
      try {
        const stored = localStorage.getItem("profile_correction_requests");
        if (stored) {
          const list = JSON.parse(stored);
          const pending = list.filter(r => r.status === "Pending").length;
          setPendingSupportCount(pending);
        } else {
          setPendingSupportCount(0);
        }
      } catch (e) {
        console.error(e);
      }
    };

    const fetchInitialCount = async () => {
      try {
        const res = await getProfileCorrections();
        if (res.status) {
          const data = res.response?.data || res.response || [];
          const list = Array.isArray(data) ? data : [];
          localStorage.setItem("profile_correction_requests", JSON.stringify(list));
          updateCount();
        }
      } catch (e) {
        console.error(e);
      }
    };

    fetchInitialCount();
    window.addEventListener("storage", updateCount);
    window.addEventListener("profile_corrections_updated", updateCount);
    return () => {
      window.removeEventListener("storage", updateCount);
      window.removeEventListener("profile_corrections_updated", updateCount);
    };
  }, []);

  const { hasPermission, loading: permissionsLoading, userData } = usePermissions();
  const [showAccessDenied, setShowAccessDenied] = useState(false);

  useEffect(() => {
    if (permissionsLoading || disableAccessCheck) return;

    const pathPermissionMap = {
      "/": "Dashboard",
      "/roles": "User Roles",
      "/employees": "Managers",
      "/categories": "Category",
      "/products": "Products",
      "/clients": "Clients",
      "/exhibitors": "Clients",
      "/unwanted-enquiries": "Unwanted Enquiries",
      "/client-groups": "Clients",
      "/client-groups/view": "Clients",
      "/flash-news": "Flash News",
      "/stall-type": "Stall Type",
      "/sources": "Source",
      "/banners": "Banner Ads",
      "/ads": "Banner Ads",
      "/upcoming-expo": "Upcoming Expo",
      "/present-expo": "Current Expo",
      "/past-expo": "Past Expo",
      "/visitor-reports": "Current Expo",
      "/interest-list": "Website Enquiries",
      "/visitor-booking-list": "Visitor Booking List",
      "/stall-booking-list": "Stall Booking List",
      "/reports/payment": "Payment",
      "/refund-list": "Refund List",
      "/reports/event-manager": "Event Manager Report",
      // "/feedback": "Feedback",
      "/careers": "Careers",
      "/contact-us": "Contact Us",
      "/reports": "Report",
      "/user-log": "User Log",
      "/user-list": "User List",
      "/today-followup": "Today Followup",
      "/generate-id": "ID Card Generator"
    };

    const currentPath = location.pathname;

    // Check if the current path (or its parent) requires a permission the user doesn't have
    const requiredPermission = Object.entries(pathPermissionMap).find(([path]) =>
      currentPath === path || (path !== "/" && currentPath.startsWith(path + "/"))
    )?.[1];

    if (requiredPermission && !hasPermission(requiredPermission)) {
      // If it's the dashboard, let's try to redirect to the first available page instead of showing modal
      if (currentPath === "/") {
        const firstAvailablePath = Object.entries(pathPermissionMap).find(([path, perm]) =>
          path !== "/" && hasPermission(perm)
        )?.[0];

        if (firstAvailablePath) {
          window.location.href = firstAvailablePath;
          return;
        }
      }
      setShowAccessDenied(true);
    } else {
      setShowAccessDenied(false);
    }
  }, [location.pathname, permissionsLoading, hasPermission]);

  const [openDropdown, setOpenDropdown] = useState(() => {
    return sessionStorage.getItem("openDropdown") || "";
  });

  useEffect(() => {
    sessionStorage.setItem("openDropdown", openDropdown);
  }, [openDropdown]);

  // Synchronize dropdown state with current URL on mount and location change
  useEffect(() => {
    const mastersPaths = ["/roles", "/employees", "/categories", "/products", "/location", "/clients", "/client-groups", "/flash-news", "/stall-type", "/sources", "/banners", "/ads", "/testimonials"];
    const expoPaths = ["/upcoming-expo", "/present-expo", "/past-expo", "/visitor-reports", "/exhibition-layout"];

    if (location.state?.from === "/exhibitors") {
      setOpenDropdown("");
    } else if (mastersPaths.some(path => location.pathname === path || location.pathname.startsWith(path + "/"))) {
      setOpenDropdown("Masters");
    } else if (expoPaths.some(path => location.pathname === path || location.pathname.startsWith(path + "/"))) {
      setOpenDropdown("Expo");
    } else {
      // For top-level links, we might want to close everything
      // But only if we're not manually opening it. 
      // Actually, standard behavior is to close others when navigating to a new section.
      const topLevelPaths = ["/", "/exhibitors", "/unwanted-enquiries", "/interest-list", "/visitor-booking-list", "/stall-booking-list", "/reports/payment", "/refund-list", "/reports/event-manager", "/user-list", "/today-followup", "/generate-id", "/feedback", "/careers", "/contact-us", "/reports", "/user-log"];
      if (topLevelPaths.some(path => location.pathname === path)) {
        setOpenDropdown("");
      }
    }
  }, [location.pathname, location.state]);

  const toggleDropdown = (name) => {
    setOpenDropdown(prev => prev === name ? "" : name);
  };

  useEffect(() => {
    // Restore scroll position
    const savedScrollPos = sessionStorage.getItem("sidebarScroll");
    if (savedScrollPos && sidebarRef.current) {
      sidebarRef.current.scrollTop = parseInt(savedScrollPos, 10);
    }

    const handleScroll = () => {
      if (sidebarRef.current) {
        sessionStorage.setItem("sidebarScroll", sidebarRef.current.scrollTop);
      }
    };

    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (sidebar) {
        sidebar.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const dateStr = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  })
    .format(currentTime)
    .replace(/ /g, "-");

  const timeStr = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(currentTime);

  const formattedTime = `${dateStr} ${timeStr}`;

  useEffect(() => {
    const fetchUserPermissions = async () => {
      const res = await getMyPermissions();
      if (res.status) {
        const storedData = localStorage.getItem("userData");
        if (storedData) {
          const userData = JSON.parse(storedData);
          const rawPerms = res.response?.data || res.response;
          const perms = Array.isArray(rawPerms)
            ? rawPerms
            : rawPerms?.permissions || [];

          // Update permissions in the stored user data
          if (userData.role && typeof userData.role === 'object') {
            userData.role.permissions = perms;
          } else {
            userData.permissions = perms;
          }

          localStorage.setItem("userData", JSON.stringify(userData));
          // Manually dispatch storage event to trigger hooks in the same tab
          window.dispatchEvent(new Event("storage"));
        }
      }
    };
    fetchUserPermissions();
  }, []);

  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
              ? "sidebar sidebar-open"
              : "sidebar"
        }
        style={{ backgroundColor: "#f7e7da" }}
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link to="/" className="sidebar-logo">
            <img
              src="/assets/images/logo.png"
              alt="site logo"
              className="light-logo"
            />
            <img
              src="/assets/images/logo.png"
              alt="site logo"
              className="dark-logo"
            />
            <img
              src="/assets/images/logo-icon1.png"
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>
        <div className="sidebar-menu-area" ref={sidebarRef}>
          <ul className="sidebar-menu" id="sidebar-menu">
            {/* Dashboard */}
            {hasPermission("Dashboard") && (
              <li>
                <NavLink
                  to="/"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-dashboard-line menu-icon" />
                  <span>Dashboard</span>
                </NavLink>
              </li>
            )}

            <li className={`dropdown ${openDropdown === "Masters" ? "open" : ""}`}>
              <Link
                to="#"
                onClick={(e) => { e.preventDefault(); toggleDropdown("Masters"); }}
                className={
                  location.pathname === "/employees" ||
                    location.pathname === "/products" ||
                    location.pathname === "/clients" ||
                    location.pathname.startsWith("/client-groups") ||
                    location.pathname === "/flash-news" ||
                    location.pathname === "/categories" ||
                    location.pathname === "/stall-type" ||
                    location.pathname === "/sources" ||
                    location.pathname === "/banners" ||
                    location.pathname === "/ads" ||
                    location.pathname === "/location" ||
                    location.pathname === "/testimonials" ||
                    location.pathname.startsWith("/products/categories")
                    ? "fw-bold underline-none"
                    : "text-dark"
                }
                style={{
                  display: (
                    hasPermission("User Roles") ||
                    hasPermission("Managers") ||
                    hasPermission("Category") ||
                    hasPermission("Products") ||
                    hasPermission("Location") ||
                    hasPermission("Clients") ||
                    hasPermission("Flash News") ||
                    hasPermission("Stall Type") ||
                    hasPermission("Source") ||
                    hasPermission("Banner Ads") ||
                    hasPermission("Testimonials")
                  ) ? "flex" : "none",
                  color:
                    location.pathname === "/employees" ||
                      location.pathname === "/products" ||
                      location.pathname === "/clients" ||
                      location.pathname.startsWith("/client-groups") ||
                      location.pathname === "/flash-news" ||
                      location.pathname === "/location" ||
                      location.pathname === "/categories" ||
                      location.pathname === "/stall-type" ||
                      location.pathname === "/sources" ||
                      location.pathname === "/banners" ||
                      location.pathname === "/ads" ||
                      location.pathname === "/testimonials" ||
                      location.pathname.startsWith("/products/categories")
                      ? "#ed1c24"
                      : "inherit",
                }}
              >
                <i
                  className="ri-cloud-line menu-icon"
                  style={{
                    color:
                      location.pathname === "/employees" ||
                        location.pathname === "/products" ||
                        location.pathname === "/clients" ||
                        location.pathname.startsWith("/client-groups") ||
                        location.pathname === "/flash-news" ||
                        location.pathname === "/location" ||
                        location.pathname === "/categories" ||
                        location.pathname === "/stall-type" ||
                        location.pathname === "/sources" ||
                        location.pathname === "/banners" ||
                        location.pathname === "/ads" ||
                        location.pathname === "/testimonials" ||
                        location.pathname.startsWith("/products/categories")
                        ? "#ed1c24"
                        : "inherit",
                  }}
                />
                <span>Masters</span>
              </Link>
              <ul
                className="sidebar-submenu"
                style={{
                  backgroundColor: "#f7e7da",
                  maxHeight: openDropdown === "Masters" ? "1000px" : "0px",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease-in-out"
                }}
              >
                {hasPermission("User Roles") && (
                  <li>
                    <NavLink
                      to="/roles"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/roles")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-shield-user-line circle-icon text-primary-600 w-auto" />{" "}
                      User Roles
                    </NavLink>
                  </li>
                )}
                {hasPermission("Exhibitors") && (
                  <li>
                    <NavLink
                      to="/client-groups"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/client-groups")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-group-2-line circle-icon text-primary-600 w-auto" />{" "}
                      Database Groups
                    </NavLink>
                  </li>
                )}
                {hasPermission("Managers") && (
                  <li>
                    <NavLink
                      to="/employees"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/employees")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-user-settings-line circle-icon text-primary-600 w-auto" />{" "}
                      Managers
                    </NavLink>
                  </li>
                )}
                {hasPermission("Category") && (
                  <li>
                    <NavLink
                      to="/categories"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/categories")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-list-settings-line circle-icon text-primary-600 w-auto" />{" "}
                      Exhibition ​Type
                    </NavLink>
                  </li>
                )}
                {hasPermission("Products") && (
                  <li>
                    <NavLink
                      to="/products"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/products")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-shopping-cart-line circle-icon text-primary-600 w-auto" />{" "}
                      Products
                    </NavLink>
                  </li>
                )}
                {hasPermission("Location") && (
                  <li>
                    <NavLink
                      to="/location"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/location")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-map-pin-line circle-icon text-primary-600 w-auto" />{" "}
                      Location
                    </NavLink>
                  </li>
                )}
                {/* {hasPermission("Clients") && (
                  <li>
                    <NavLink
                      to="/clients"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/clients")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-group-line circle-icon text-primary-600 w-auto" />{" "}
                      Clients
                    </NavLink>
                  </li>
                )} */}
                {/* {hasPermission("Flash News") && (
                  <li>
                    <NavLink
                      to="/flash-news"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/flash-news")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-notification-3-line circle-icon text-primary-600 w-auto" />{" "}
                      Flash News
                    </NavLink>
                  </li>
                )} */}
                {hasPermission("Flash News") && (
                  <li>
                    <NavLink
                      to="/ads"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/ads")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-advertisement-line circle-icon text-primary-600 w-auto" />{" "}
                      Flash News
                    </NavLink>
                  </li>
                )}

                {hasPermission("Stall Type") && (
                  <li>
                    <NavLink
                      to="/stall-type"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/stall-type")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-layout-grid-line circle-icon text-primary-600 w-auto" />{" "}
                      Stall Type
                    </NavLink>
                  </li>
                )}
                {hasPermission("Source") && (
                  <li>
                    <NavLink
                      to="/sources"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/sources")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-share-forward-line circle-icon text-primary-600 w-auto" />{" "}
                      Source
                    </NavLink>
                  </li>
                )}
                {hasPermission("Banner Ads") && (
                  <li>
                    <NavLink
                      to="/banners"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/banners")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-image-line circle-icon text-primary-600 w-auto" />{" "}
                      Banner Ads
                    </NavLink>
                  </li>
                )}
                {hasPermission("Testimonials") && (
                  <li>
                    <NavLink
                      to="/testimonials"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/testimonials")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-video-line circle-icon text-primary-600 w-auto" />{" "}
                      Testimonials
                    </NavLink>
                  </li>
                )}

              </ul>
            </li>

            {/* Expo Submenu */}
            <li className={`dropdown ${openDropdown === "Expo" ? "open" : ""}`}>
              <Link
                to="#"
                onClick={(e) => { e.preventDefault(); toggleDropdown("Expo"); }}
                className={
                  location.pathname.startsWith("/upcoming-expo") ||
                    location.pathname === "/past-expo" ||
                    location.pathname === "/present-expo" ||
                    location.pathname === "/exhibition-layout" ||
                    location.pathname === "/visitor-reports"
                    ? "fw-bold"
                    : "text-dark"
                }
                style={{
                  display: (
                    hasPermission("Upcoming Expo") ||
                    hasPermission("Current Expo") ||
                    hasPermission("Past Expo")
                  ) ? "flex" : "none",
                  color:
                    location.pathname.startsWith("/upcoming-expo") ||
                      location.pathname === "/past-expo" ||
                      location.pathname === "/present-expo" ||
                      location.pathname === "/exhibition-layout" ||
                      location.pathname === "/visitor-reports"
                      ? "#ed1c24"
                      : "inherit",
                }}
              >
                <i
                  className="ri-calendar-line menu-icon"
                  style={{
                    color:
                      location.pathname === "/upcoming-expo" ||
                        location.pathname === "/present-expo" ||
                        location.pathname === "/past-expo" ||
                        location.pathname === "/visitor-reports" ||
                        location.pathname === "/exhibition-layout"
                        ? "#ed1c24"
                        : "inherit",
                  }}
                />
                <span>Expo</span>
              </Link>
              <ul
                className="sidebar-submenu"
                style={{
                  backgroundColor: "#f7e7da",
                  maxHeight: openDropdown === "Expo" ? "1000px" : "0px",
                  overflow: "hidden",
                  transition: "max-height 0.3s ease-in-out"
                }}
              >
                {/* {hasPermission("Upcoming Expo") && (
                  <li>
                    <NavLink
                      to="/upcoming-expo"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/upcoming-expo") ||
                          location.pathname === "/exhibition-layout"
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-speed-mini-line circle-icon text-primary-600 w-auto" />{" "}
                      Upcoming Expo
                    </NavLink>
                  </li>
                )} */}
                {hasPermission("Current Expo") && (
                  <li>
                    <NavLink
                      to="/present-expo"
                      className={(navData) =>
                        navData.isActive ||
                          location.pathname.startsWith("/visitor-reports")
                          ? "active-page"
                          : ""
                      }
                    >
                      <i className="ri-calendar-line circle-icon text-primary-600 w-auto" />{" "}
                      Current Expos
                    </NavLink>
                  </li>
                )}

                {/* <li>
                  <NavLink
                    to="/visitor-reports?tab=enquiry"
                    className={(navData) =>
                      navData.isActive || location.pathname.startsWith("/visitor-reports") ? "active-page" : ""
                    }
                  >
                    <i className="ri-user-search-line circle-icon text-primary-600 w-auto" />{" "}
                    Visitor Report
                  </NavLink>
                </li> */}
                {/* <li>
                  <NavLink
                    to="/visitor-enquiry"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-user-follow-line circle-icon text-primary-600 w-auto" />{" "}
                    Visitor Enquiry
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/visitor-booked"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    <i className="ri-ticket-line circle-icon text-primary-600 w-auto" />{" "}
                    Visitor Booked
                  </NavLink>
                </li> */}
                {hasPermission("Past Expo") && (
                  <li>
                    <NavLink
                      to="/past-expo"
                      className={(navData) =>
                        navData.isActive ? "active-page" : ""
                      }
                    >
                      <i className="ri-history-line circle-icon text-primary-600 w-auto" />{" "}
                      Past Expos
                    </NavLink>
                  </li>
                )}
              </ul>
            </li>

            {/* Booking Status */}
            {/* <li>
              <NavLink to="/booking-status" className={(navData) => navData.isActive ? "active-page text-white" : "text-dark"}>
                <i className="ri-chat-3-line menu-icon" />
                <span>Booking Status</span>
              </NavLink>
            </li> */}

            {hasPermission("Unwanted Enquiries") && (
              <li>
                <NavLink
                  to="/unwanted-enquiries"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-close-circle-line menu-icon" />
                  <span>Unwanted Enquiries</span>
                </NavLink>
              </li>
            )}

            {hasPermission("Clients") && (
              <li>
                <NavLink
                  to="/exhibitors"
                  className={(navData) =>
                    navData.isActive ||
                      ((location.pathname.startsWith("/clients/view/") || location.pathname.startsWith("/clients/edit/")) && location.state?.from === "/exhibitors")
                      ? "active-page text-white"
                      : "text-dark"
                  }
                >
                  <i className="ri-group-line menu-icon" />
                  <span>Exhibitors</span>
                </NavLink>
              </li>
            )}

            {/* Visitor Booking List */}
            {/* Interest List - Moved to Top Level */}
            {/* {hasPermission("Expo Interest Log") && (
              <li>
                <NavLink
                  to="/interest-list"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-heart-line menu-icon" />
                  <span>Website Enquiries</span>
                </NavLink>
              </li>
            )} */}

            {hasPermission("Visitor Booking List") && (
              <li>
                <NavLink
                  to="/visitor-booking-list"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-user-received-2-line menu-icon" />
                  <span>Visitor Booking List</span>
                </NavLink>
              </li>
            )}

            {/* Stall Booking List */}
            {hasPermission("Stall Booking List") && (
              <li>
                <NavLink
                  to="/stall-booking-list"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-layout-grid-line menu-icon" />
                  <span>Stall Booking List</span>
                </NavLink>
              </li>
            )}

            {hasPermission("Payment Report") && (
              <li>
                <NavLink
                  to="/reports/payment"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-bank-card-line menu-icon" />
                  <span>Payment</span>
                </NavLink>
              </li>
            )}

            {/* Refund List */}
            {/* {hasPermission("Refund List") && (
              <li>
                <NavLink
                  to="/refund-list"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-refund-2-line menu-icon" />
                  <span>Refund List</span>
                </NavLink>
              </li>
            )} */}

            {hasPermission("Event Manager Report") && (
              <li>
                <NavLink
                  to="/reports/event-manager"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-user-follow-line menu-icon" />
                  <span>Event Manager Report</span>
                </NavLink>
              </li>
            )}
            {/* Report */}
            {/* <li className="dropdown">
              <Link
                to="#"
                className={
                  location.pathname.startsWith("/reports/") ||
                  location.pathname === "/enquiry-list"
                    ? "fw-bold"
                    : "text-dark"
                }
                style={{
                  color:
                    location.pathname.startsWith("/reports/") ||
                    location.pathname === "/enquiry-list"
                      ? "#ed1c24"
                      : "inherit",
                }}
              >
                <i
                  className="ri-apps-2-line menu-icon"
                  style={{
                    color:
                      location.pathname.startsWith("/reports/") ||
                      location.pathname === "/enquiry-list"
                        ? "#ed1c24"
                        : "inherit",
                  }}
                />
                <span>Report</span>
              </Link>
              <ul
                className="sidebar-submenu"
                style={{ backgroundColor: "#f7e7da" }}
              >
                <li>
                  <NavLink
                    to="/reports/event-manager"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    Event Manager
                  </NavLink>
                </li>
                <li><NavLink to="/reports/back-end" className={(navData) => navData.isActive ? "active-page" : ""}>Backend Report</NavLink></li>
                <li><NavLink to="/reports/booked" className={(navData) => navData.isActive ? "active-page" : ""}>Booked Report</NavLink></li>
                <li>
                  <NavLink
                    to="/reports/client-data-entry"
                    className={(navData) =>
                      navData.isActive ? "active-page" : ""
                    }
                  >
                    Client Entry Report
                  </NavLink>
                </li>
                <li><NavLink to="/enquiry-list" className={(navData) => navData.isActive ? "active-page" : ""}>Enquiry List</NavLink></li>
              </ul>
            </li> */}

            {/* CRM */}
            {/* <li className="dropdown">
              <Link to="#"
                className={
                  location.pathname === "/assigned" ||
                    location.pathname === "/prospects" ||
                    location.pathname === "/correction" ||
                    location.pathname === "/not-interest" ||
                    location.pathname === "/over-due" ||
                    location.pathname === "/booked-details"
                    ? "fw-bold"
                    : "text-dark"
                }
                style={{
                  color: (location.pathname === "/assigned" ||
                    location.pathname === "/prospects" ||
                    location.pathname === "/correction" ||
                    location.pathname === "/not-interest" ||
                    location.pathname === "/over-due" ||
                    location.pathname === "/booked-details") ? "#ed1c24" : "inherit"
                }}
              >
                <i
                  className="ri-customer-service-line menu-icon"
                  style={{
                    color: (location.pathname === "/assigned" ||
                      location.pathname === "/prospects" ||
                      location.pathname === "/correction" ||
                      location.pathname === "/not-interest" ||
                      location.pathname === "/over-due" ||
                      location.pathname === "/booked-details") ? "#ed1c24" : "inherit"
                  }}
                />
                <span>CRM</span>
              </Link>
              <ul className="sidebar-submenu" style={{ backgroundColor: "#f7e7da" }}>
                <li><NavLink to="/assigned" className={(navData) => navData.isActive ? "active-page" : ""}>Assigned</NavLink></li>
                <li><NavLink to="/prospects" className={(navData) => navData.isActive ? "active-page" : ""}>Prospects</NavLink></li>
                <li><NavLink to="/correction" className={(navData) => navData.isActive ? "active-page" : ""}>Correction</NavLink></li>
                <li><NavLink to="/not-interest" className={(navData) => navData.isActive ? "active-page" : ""}>Not Interest</NavLink></li>
                <li><NavLink to="/over-due" className={(navData) => navData.isActive ? "active-page" : ""}>Over Due</NavLink></li>
                <li><NavLink to="/booked-details" className={(navData) => navData.isActive ? "active-page" : ""}>Booked Details</NavLink></li>
              </ul>
            </li> */}

            {/* Past Participants */}
            {/* <li>
              <NavLink
                to="/past-participants"
                className={(navData) =>
                  navData.isActive ? "active-page text-white" : "text-dark"
                }
              >
                <i className="ri-group-line menu-icon" />
                <span>Past Participants</span>
              </NavLink>
            </li> */}

            {/* User Log */}


            {/* User List (Mobile App) */}
            {hasPermission("User List") && (
              <li>
                <NavLink
                  to="/user-list"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-user-star-line menu-icon" />
                  <span>User List</span>
                </NavLink>
              </li>
            )}

            {/* Today Followup */}
            {hasPermission("Today Followup") && (
              <li>
                <NavLink
                  to="/today-followup"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-id-card-line menu-icon" />
                  <span>Today Followup</span>
                </NavLink>
              </li>
            )}

            {/* ID Card Generator */}
            {hasPermission("ID Card Generator") && (
              <li>
                <NavLink
                  to="/generate-id"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-account-box-line menu-icon" />
                  <span>ID Card Generator</span>
                </NavLink>
              </li>
            )}

            {/* Feedback */}
            {/* {hasPermission("Feedback") && (
              <li>
                <NavLink
                  to="/feedback"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-feedback-line menu-icon" />
                  <span>Feedback</span>
                </NavLink>
              </li>
            )} */}

            {hasPermission("Careers") && (
              <li>
                <NavLink
                  to="/careers"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-briefcase-line menu-icon" />
                  <span>Careers</span>
                </NavLink>
              </li>
            )}

            {hasPermission("Contact Us") && (
              <li>
                <NavLink
                  to="/contact-us"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-mail-line menu-icon" />
                  <span>Contact Us</span>
                </NavLink>
              </li>
            )}

            {hasPermission("Report") && (
              <li>
                <NavLink
                  to="/reports"
                  end
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-file-chart-line menu-icon" />
                  <span>Report</span>
                </NavLink>
              </li>
            )}

            {/* User Log */}
            {hasPermission("User Log") && (
              <li>
                <NavLink
                  to="/user-log"
                  className={(navData) =>
                    navData.isActive ? "active-page text-white" : "text-dark"
                  }
                >
                  <i className="ri-user-settings-line menu-icon" />
                  <span>User Log</span>
                </NavLink>
              </li>
            )}

            {/* Settings */}
            {/* <li className="dropdown">
              <Link to="#"
                className={
                  location.pathname === "/change-password"
                    ? "active-page text-white"
                    : "text-dark"
                }
                style={{
                  backgroundColor: (location.pathname === "/change-password") ? "#ed1c24" : "transparent"
                }}
              >
                <i className="ri-settings-3-line menu-icon" />
                <span>Settings</span>
              </Link>
              <ul className="sidebar-submenu" style={{ backgroundColor: "#f7e7da" }}>
                <li><NavLink to="/change-password">Change Password</NavLink></li>
              </ul>
            </li> */}

            {/* Support - Profile Correction Requests */}
            {hasPermission("Support") && (
              <li>
                <NavLink
                  to="/profile-corrections"
                  className={(navData) =>
                    navData.isActive || location.pathname.startsWith("/profile-corrections")
                      ? "active-page text-white"
                      : "text-dark"
                  }
                >
                  <i className="ri-customer-service-2-line menu-icon" />
                  <span className="d-flex align-items-center justify-content-between w-100">
                    <span>Support</span>
                    {pendingSupportCount > 0 && (
                      <span
                        className="badge rounded-pill bg-danger text-white px-8 py-4 ms-2"
                        style={{ fontSize: "11px", fontWeight: "bold", minWidth: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        {pendingSupportCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              </li>
            )}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <span
                  style={{
                    color: "#ed1c24",
                    fontWeight: "600",
                    fontSize: "16px",
                  }}
                >
                  {formattedTime}
                </span>
                {/* <ThemeToggleButton /> */}
                <div className="dropdown d-none d-sm-inline-block">
                  {/* <button
                    className="has-indicator w-40-px h-40-px bg-neutral-200 rounded-circle d-flex justify-content-center align-items-center"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <Icon
                      icon="iconoir:bell"
                      className="text-primary-light text-xl"
                    />
                  </button> */}
                  <div className="dropdown-menu to-top dropdown-menu-lg p-0">
                    <div className="m-16 py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-0">
                          Notifications
                        </h6>
                      </div>
                      <span className="text-primary-600 fw-semibold text-lg w-40-px h-40-px rounded-circle bg-base d-flex justify-content-center align-items-center">
                        05
                      </span>
                    </div>
                    <div className="max-h-400-px overflow-y-auto scroll-sm pe-4">
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <Icon
                              icon="bitcoin-icons:verify-outline"
                              className="icon text-xxl"
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Congratulations
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Your profile has been Verified. Your profile has
                              been Verified
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <img
                              src="/assets/images/notification/profile-1.png"
                              alt=""
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Ronald Richards
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              You can stitch between artboards
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-info-subtle text-info-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            AM
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Arlene McCoy
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between bg-neutral-50"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-success-subtle text-success-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            <img
                              src="/assets/images/notification/profile-2.png"
                              alt=""
                            />
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Annette Black
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                      <Link
                        to="#"
                        className="px-24 py-12 d-flex align-items-start gap-3 mb-2 justify-content-between"
                      >
                        <div className="text-black hover-bg-transparent hover-text-primary d-flex align-items-center gap-3">
                          <span className="w-44-px h-44-px bg-info-subtle text-info-main rounded-circle d-flex justify-content-center align-items-center flex-shrink-0">
                            DR
                          </span>
                          <div>
                            <h6 className="text-md fw-semibold mb-4">
                              Darlene Robertson
                            </h6>
                            <p className="mb-0 text-sm text-secondary-light text-w-200-px">
                              Invite you to prototyping
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-secondary-light flex-shrink-0">
                          23 Mins ago
                        </span>
                      </Link>
                    </div>
                    <div className="text-center py-12 px-16">
                      <Link
                        to="#"
                        className="text-primary-600 fw-semibold text-md hover-text-primary d-flex justify-content-center align-items-center gap-2"
                      >
                        See All Notifications
                        <Icon
                          icon="solar:arrow-right-linear"
                          className="icon"
                        />
                      </Link>
                    </div>
                  </div>
                </div>
                {/* Notification dropdown end */}

                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle border-0 p-0"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <div
                      className="w-40-px h-40-px bg-primary-600 text-white rounded-circle d-flex justify-content-center align-items-center fw-bold shadow-sm"
                      style={{ fontSize: "18px" }}
                    >
                      {userData?.name?.charAt(0).toUpperCase() || "A"}
                    </div>
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm p-0">
                    <div className="p-16 px-24 border-bottom">
                      <h6 className="text-lg fw-semibold text-primary-light mb-0">
                        {userData?.name || "Administrator"}
                      </h6>
                      <span className="text-secondary-light text-sm">
                        {typeof userData?.role === 'string'
                          ? userData.role
                          : userData?.roleId?.name ||
                          userData?.role?.name ||
                          "Admin"}
                      </span>
                    </div>
                    <ul className="p-16">
                      {/* <li>
                        <Link
                          to="/my-profile"
                          className="d-flex align-items-center gap-3 hover-bg-primary-50 text-secondary-light radius-8 px-12 py-12"
                        >
                          <Icon
                            icon="solar:user-circle-outline"
                            className="icon text-xl"
                          />
                          My Profile
                        </Link>
                      </li> */}
                      <li>
                        <button
                          onClick={() => setShowLogoutModal(true)}
                          className="d-flex align-items-center gap-3 hover-bg-primary-50 text-secondary-light radius-8 px-12 py-12 border-0 bg-transparent w-100"
                        >
                          <Icon
                            icon="solar:logout-2-outline"
                            className="icon text-xl"
                          />
                          Log Out
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className="dashboard-main-body">{children}</div>

        {/* Footer section */}
        <footer className="d-footer">
          <div className="row align-items-center">
            <div className="col-12 d-flex justify-content-between">
              {/* Left Side */}
              <p className="mb-0 text-start">
                © {new Date().getFullYear()}{" "}
                <span className="text-primary-600">Prompt Trade Fairs.</span>{" "}
                All Rights Reserved.
              </p>

              {/* Right Side */}
              <p className="mb-0 text-end">
                Developed & Maintained By{" "}
                <span className="text-primary-600" onClick={() => window.open('https://www.oceansoftwares.com', '_blank')} style={{ cursor: 'pointer' }}>Oceansoftwares Pvt Ltd.</span>
              </p>
            </div>
          </div>
        </footer>
      </main>

      {/* Logout Confirmation Modal */}
      <Modal
        show={showLogoutModal}
        onHide={() => setShowLogoutModal(false)}
        centered
        backdropClassName="modal-backdrop-blur"
        contentClassName="radius-16 border-0 shadow-lg"
        style={{ width: '100%' }}
        dialogClassName="mw-100 w-600-px mx-auto"
      >
        <Modal.Body className="text-center p-32">
          <div className="d-flex justify-content-center mb-24">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:logout-3-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="fw-bold mb-8">Confirm Logout</h5>
          <p className="text-secondary-light mb-24">Are you sure you want to logout from your session?</p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-secondary" className="px-24 radius-8" onClick={() => setShowLogoutModal(false)}>Cancel</Button>
            <Button
              className="px-24 radius-8 border-0 fw-bold"
              onClick={() => {
                toast.success("Logged out successfully!", { autoClose: 1500 });
                setTimeout(() => {
                  localStorage.clear();
                  window.location.href = "/sign-in";
                }, 1500);
              }}
              style={{ backgroundColor: "#ed1c24", color: "white" }}
            >
              Logout
            </Button>
          </div>
        </Modal.Body>
      </Modal>
      {/* Access Denied Modal - Compact Centered Pro Card with Maximum Privacy Backdrop */}
      <style>
        {`
          .access-denied-backdrop {
            background-color: rgba(0, 0, 0, 34%) !important;
            backdrop-filter: blur(25px) !important;
            -webkit-backdrop-filter: blur(25px) !important;
            opacity: 1 !important;
          }
        `}
      </style>
      <Modal
        show={showAccessDenied}
        onHide={() => {
          const pathPermissionMap = {
            "/": "Dashboard", "/roles": "User Roles", "/employees": "Managers", "/categories": "Category",
            "/products": "Products", "/location": "Location", "/clients": "Clients", "/flash-news": "Flash News",
            "/stall-type": "Stall Type", "/sources": "Source", "/banners": "Banner Ads", "/ads": "Banner Ads", "/upcoming-expo": "Upcoming Expo",
            "/present-expo": "Current Expo", "/past-expo": "Past Expo", "/visitor-reports": "Current Expo",
            "/interest-list": "Website Enquiries", "/visitor-booking-list": "Visitor Booking List",
            "/stall-booking-list": "Stall Booking List", "/reports/payment": "Payment Report", "/refund-list": "Refund List", "/reports/event-manager": "Event Manager Report",
            "/feedback": "Feedback", "/careers": "Careers", "/contact-us": "Contact Us", "/reports": "Report",
            "/user-log": "User Log", "/user-list": "User List", "/today-followup": "Today Followup", "/generate-id": "ID Card Generator"
          };
          const firstPath = Object.entries(pathPermissionMap).find(([path, perm]) => path !== "/" && hasPermission(perm))?.[0] || "/";
          window.location.href = firstPath;
        }}
        centered
        backdrop="static"
        keyboard={false}
        backdropClassName="access-denied-backdrop"
        contentClassName="border-0 shadow-lg mx-auto"
        style={{ zIndex: 9999 }}
        dialogClassName="w-380-px"
      >
        <Modal.Body className="p-0 overflow-hidden" style={{ borderRadius: "12px", backgroundColor: "white" }}>
          {/* Top Accent Bar */}
          <div style={{ height: "4px", backgroundColor: "#ed1c24" }}></div>

          <div className="text-center p-32">
            <div className="d-flex justify-content-center mb-20">
              <div className="rounded-circle d-flex justify-content-center align-items-center w-56-px h-56-px" style={{ backgroundColor: "#fff5f5" }}>
                <Icon icon="solar:shield-warning-bold" style={{ color: "#ed1c24", fontSize: "32px" }} />
              </div>
            </div>

            <h4 className="fw-bold mb-8" style={{ color: "#1a1a1a", letterSpacing: "-0.5px" }}>Access Restricted</h4>
            <p className="mb-24 text-secondary-light px-2" style={{ fontSize: "14px", lineHeight: "1.5" }}>
              You do not have the required permissions to view this module.
            </p>

            <div className="d-flex justify-content-center">
              <Button
                className="px-32 py-10 radius-8 border-0 fw-bold shadow-sm"
                onClick={() => {
                  const pathPermissionMap = {
                    "/": "Dashboard", "/roles": "User Roles", "/employees": "Managers", "/categories": "Category",
                    "/products": "Products", "/location": "Location", "/clients": "Clients", "/flash-news": "Flash News",
                    "/stall-type": "Stall Type", "/sources": "Source", "/banners": "Banner Ads", "/ads": "Banner Ads", "/upcoming-expo": "Upcoming Expo",
                    "/present-expo": "Current Expo", "/past-expo": "Past Expo", "/visitor-reports": "Current Expo",
                    "/interest-list": "Website Enquiries", "/visitor-booking-list": "Visitor Booking List",
                    "/stall-booking-list": "Stall Booking List", "/reports/payment": "Payment Report", "/refund-list": "Refund List", "/reports/event-manager": "Event Manager Report",
                    "/feedback": "Feedback", "/careers": "Careers", "/contact-us": "Contact Us", "/reports": "Report",
                    "/user-log": "User Log", "/user-list": "User List", "/today-followup": "Today Followup", "/generate-id": "ID Card Generator"
                  };
                  const firstPath = Object.entries(pathPermissionMap).find(([path, perm]) => path !== "/" && hasPermission(perm))?.[0] || "/";
                  window.location.href = firstPath;
                }}
                style={{
                  backgroundColor: "#ed1c24",
                  color: "white",
                  fontSize: "14px"
                }}
              >
                Go to Accessible Page
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </section>
  );
};

export default MasterLayout;
